import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import omit from 'lodash/omit';
import keys from 'lodash/keys';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { useDispatch } from 'react-redux';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import styles from './BundleVersionListRevisionRowDetails.style';
import { useBundleVersionContext } from '../../context/bundle-version';
import { editBundle } from '../../actions/bundles';
import ReviseBundleForm from '../ReviseBundleForm';
import ReviseBundleDialog from '../ReviseBundleDialog';
import {
  orphanBundleContent,
  bundleReprocessContent,
  dateValidationMessage,
} from '../../constants';

const BundleVersionListRevisionRowDetails = React.forwardRef(
  ({ bundleVersion, onUpdate, onSuccess }, ref) => {
    const { isOpen } = useBundleVersionContext();

    const className = clsx(styles.root, styles.bundleInfo, {
      [styles.isClosed]: !isOpen,
      [styles.isOpen]: isOpen,
    });

    const dispatch = useDispatch();

    const componentAllocationKeys = {
      allocationPercentage: 0,
      isRoyaltyBearing: false,
      productId: '',
      tokenId: '',
    };

    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dialogContent, setDialogContent] = useState(orphanBundleContent);

    const handleShowDialog = type => {
      if (type === 'reprocess') {
        setDialogContent(bundleReprocessContent);
      } else {
        setDialogContent(orphanBundleContent);
      }
      setIsDialogVisible(true);
    };

    const updateBundle = async () => {
      let result;
      const { values, setErrors } = ref?.current;
      if (values && setErrors) {
        const submittedValues = omit(values, ['_id', 'version', 'errorFields', 'transactionCount']);
        submittedValues.componentAllocationPercentage =
          submittedValues?.componentAllocationPercentage?.map(prod =>
            pick(prod, keys(componentAllocationKeys)),
          );
        result = await dispatch(editBundle(submittedValues));
        if (result?.error && result?.payload?.message) {
          const errorMessage =
            result.payload.message === dateValidationMessage
              ? result.payload.message
                  .replace('endDate', 'Expiration Date')
                  .replace('ref:startDate', 'Effective Start Date')
              : result.payload.message;
          setErrors({ form: errorMessage });
        }
      }
      setIsDialogVisible(false);
      // if no errors and
      if (!result?.error) {
        onSuccess(get(result, `payload.entities.bundles.${result?.payload?.result}`));
      }

      return result;
    };

    const handleDialogSubmit = async () => {
      if (dialogContent.submitType === 'save') {
        setIsSubmitting(true);
        await updateBundle();
        setIsSubmitting(false);
      } else {
        setIsDialogVisible(false);
      }
    };

    const onReviseSave = () => {
      const { values } = ref?.current;
      let result;
      const startDate = values && new Date(values.startDate);
      if (startDate <= new Date() && !isDialogVisible) {
        handleShowDialog('reprocess');
      } else {
        result = updateBundle();
      }

      return result;
    };

    return (
      <>
        <div className={className}>
          <ReviseBundleForm
            bundleVersion={bundleVersion}
            ref={ref}
            onUpdate={onUpdate}
            onSuccess={onSuccess}
            handleShowDialog={handleShowDialog}
            onReviseSave={onReviseSave}
          />
          <ReviseBundleDialog
            onDismiss={() => setIsDialogVisible(false)}
            onSubmit={handleDialogSubmit}
            isSubmitting={isSubmitting}
            hidden={!isDialogVisible}
            dialogContentProps={{
              type: DialogType.close,
              title: dialogContent.title,
              closeButtonAriaLabel: 'Close',
            }}
            dialogBodyContent={{
              content: dialogContent.content,
              buttonText: dialogContent.button,
              showCancelButton: dialogContent.showCancelButton,
            }}
          />
        </div>
      </>
    );
  },
);

BundleVersionListRevisionRowDetails.propTypes = {
  bundleVersion: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    componentAllocationPercentage: PropTypes.arrayOf(
      PropTypes.shape({
        productName: PropTypes.string,
        tokenId: PropTypes.number,
        isRoyaltyBearing: PropTypes.bool,
        allocationPercentage: PropTypes.number,
      }),
    ),
  }),
  onUpdate: PropTypes.func,
  onSuccess: PropTypes.func,
};

BundleVersionListRevisionRowDetails.defaultProps = {
  bundleVersion: {
    startDate: '',
    endDate: '',
    componentAllocationPercentage: [],
  },
  onUpdate: () => {},
  onSuccess: () => {},
};

export default BundleVersionListRevisionRowDetails;
